/**
 * TOSS Web Application - Enable Notifications Banner
 *
 * Floating banner prompting the user to enable browser push notifications.
 *
 * Visibility rules:
 * - Shown only when `usePushNotifications().shouldPromptForPermission` is true
 *   (authenticated + browser supports push + permission is 'default' + user
 *    has not opted out via profile settings)
 * - Hidden for 7 days after the user clicks "Позже"
 * - Hidden automatically after the user grants permission (permission flips
 *   from 'default' to 'granted', which collapses `shouldPromptForPermission`)
 *
 * Position: bottom-right on desktop, bottom on mobile — mirrors `PWAUpdateBanner`
 * so the two floating banners stack predictably.
 */

import { useCallback, useState } from 'react';
import { Bell, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { usePushNotifications } from '@/hooks/usePushNotifications';

const STORAGE_KEY = 'push-prompt-dismissed-at';
const DISMISS_DAYS = 7;

function isDismissed(): boolean {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (!value) return false;

    const dismissedAt = parseInt(value, 10);
    if (Number.isNaN(dismissedAt)) return false;

    const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    return daysSince < DISMISS_DAYS;
  } catch {
    // localStorage might throw in private mode — treat as not dismissed.
    return false;
  }
}

export function EnableNotificationsBanner(): JSX.Element | null {
  const { shouldPromptForPermission, requestPermission, isRequesting } =
    usePushNotifications();
  const [dismissed, setDismissed] = useState<boolean>(() => isDismissed());

  const handleEnable = useCallback(async () => {
    const granted = await requestPermission();
    if (granted) {
      // Clear any lingering "Позже" timestamp so the banner never reappears
      // on this device once permission is granted.
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    }
    // No need to setDismissed(true): when permission flips to 'granted',
    // shouldPromptForPermission becomes false and the banner unmounts itself.
  }, [requestPermission]);

  const handleLater = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    setDismissed(true);
  }, []);

  if (!shouldPromptForPermission || dismissed) {
    return null;
  }

  return (
    <div
      className="fixed bottom-20 left-4 right-4 md:bottom-4 md:left-auto md:right-4 md:max-w-sm z-50 animate-slide-up"
      role="dialog"
      aria-live="polite"
      aria-label="Включить уведомления"
    >
      <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg shadow-[var(--shadow-overlay)] p-4">
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-[var(--color-bg-secondary)] flex items-center justify-center text-[var(--color-primary-500)]">
            <Bell className="w-5 h-5" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[var(--color-text-primary)]">
              Включить уведомления
            </p>
            <p className="text-xs text-[var(--color-text-secondary)] mt-1 leading-snug">
              Хотите получать уведомления о новых сообщениях в чате и других
              событиях?
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Button
                variant="primary"
                size="sm"
                onClick={handleEnable}
                isLoading={isRequesting}
                loadingText="Запрос..."
              >
                Включить
              </Button>
              <Button variant="ghost" size="sm" onClick={handleLater}>
                Позже
              </Button>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLater}
            className="shrink-0 -mt-1 -mr-1 p-1 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
            aria-label="Закрыть"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default EnableNotificationsBanner;
